import { useEffect, useRef, useState } from 'react';
import api from '../services/milk-api';

declare global {
  interface Window {
    google?: any;
  }
}

interface LoginPageProps {
  onLogin: (user: { name: string; role: string; tenantId: number; userId: number; tenantName: string; mustChangePassword?: boolean; subscriptionWarningMessage?: string | null }) => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [emailOrMobile, setEmailOrMobile] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [tenantsToSelect, setTenantsToSelect] = useState<{ tenantId: number; tenantName: string }[] | null>(null);
  const googleBtnRef = useRef<HTMLDivElement | null>(null);
  const googleClientId = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID as string | undefined;

  const completeLogin = (data: { token: string; role: string; tenantId: number; userId: number; name: string; tenantName: string; mustChangePassword?: boolean; subscriptionWarningMessage?: string | null }) => {
    api.setToken(data.token);
    localStorage.setItem('milk_user', JSON.stringify({
      name: data.name,
      role: data.role,
      tenantId: data.tenantId,
      userId: data.userId,
      tenantName: data.tenantName,
    }));
    onLogin({
      name: data.name,
      role: data.role,
      tenantId: data.tenantId,
      userId: data.userId,
      tenantName: data.tenantName,
      mustChangePassword: data.mustChangePassword,
      subscriptionWarningMessage: data.subscriptionWarningMessage,
    });
  };

  useEffect(() => {
    if (!googleClientId) return;

    const loadGoogle = () => {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response: { credential?: string }) => {
          if (!response.credential) {
            setError('Google login did not return a credential');
            return;
          }

          setError('');
          setLoading(true);
          try {
            const res = await api.loginWithGoogle(response.credential);
            if (res.success && res.data) {
              completeLogin(res.data);
            } else {
              setError(res.message || 'Google login failed');
            }
          } catch {
            setError('Google login failed. Check API and Google client setup.');
          } finally {
            setLoading(false);
          }
        },
      });

      googleBtnRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        width: 360,
        text: 'continue_with',
      });
      setGoogleReady(true);
    };

    const existing = document.querySelector('script[data-google-identity="true"]') as HTMLScriptElement | null;
    if (existing) {
      if (window.google) loadGoogle();
      else existing.addEventListener('load', loadGoogle, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-google-identity', 'true');
    script.onload = loadGoogle;
    document.head.appendChild(script);
  }, [googleClientId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await api.login(emailOrMobile, password);
      if (res.success && res.data) {
        // Account exists in multiple dairies with this password → ask which dairy to sign in to
        if (res.data.requiresTenantSelection && res.data.tenantOptions && res.data.tenantOptions.length > 0) {
          setTenantsToSelect(res.data.tenantOptions);
        } else {
          completeLogin(res.data);
        }
      } else {
        setError(res.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Is the API running?');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSelect = async (tenant: { tenantId: number; tenantName: string }) => {
    setError('');
    setLoading(true);
    try {
      const res = await api.selectTenant(emailOrMobile, password, tenant.tenantId);
      if (res.success && res.data) {
        setTenantsToSelect(null);
        completeLogin(res.data);
      } else {
        setError(res.message || 'Login failed for selected dairy');
      }
    } catch {
      setError('Network error. Is the API running?');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      background: 'linear-gradient(135deg, #1B5E20 0%, #43A047 50%, #66BB6A 100%)',
    }}>
      <div style={{ width: '100%', maxWidth: '420px', padding: '0 1rem' }}>
        {/* Logo / Branding */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%', background: 'white',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}>
            🥛
          </div>
          <h1 style={{ color: 'white', fontSize: '1.75rem', marginTop: '1rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
            Milk Subscription
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
            Fresh dairy, delivered daily
          </p>
        </div>

        {/* Login Card */}
        <div style={{
          background: 'white', padding: '2rem', borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}>
          <h2 style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#2E7D32', fontSize: '1.2rem', fontWeight: 600 }}>
            Sign in to your account
          </h2>

          {error && (
            <div style={{
              background: '#FFEBEE', color: '#C62828', padding: '0.75rem 1rem',
              borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem',
              border: '1px solid #FFCDD2',
            }}>
              {error}
            </div>
          )}

          {tenantsToSelect ? (
            <div>
              <h3 style={{ textAlign: 'center', marginBottom: '0.5rem', color: '#2E7D32', fontSize: '1.05rem', fontWeight: 600 }}>
                Which dairy would you like to sign in to?
              </h3>
              <p style={{ textAlign: 'center', color: '#777', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Your account exists in {tenantsToSelect.length} dairy{tenantsToSelect.length > 1 ? 'ies' : 'y'}
              </p>
              {tenantsToSelect.map(t => (
                <button
                  key={t.tenantId}
                  type="button"
                  disabled={loading}
                  onClick={() => handleTenantSelect(t)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.85rem 1rem', marginBottom: '0.6rem', background: '#F1F8E9',
                    border: '2px solid #C5E1A5', borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '0.95rem', fontWeight: 600, color: '#1B5E20', transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { if (!loading) (e.target as HTMLElement).style.borderColor = '#43A047'; }}
                  onMouseLeave={e => { if (!loading) (e.target as HTMLElement).style.borderColor = '#C5E1A5'; }}
                >
                  <span>🥛 {t.tenantName}</span>
                  <span style={{ fontSize: '0.9rem' }}>→</span>
                </button>
              ))}
              <button
                type="button"
                disabled={loading}
                onClick={() => { setTenantsToSelect(null); setError(''); }}
                style={{
                  width: '100%', padding: '0.75rem', background: 'transparent', color: '#777',
                  border: '1px solid #E0E0E0', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem', fontWeight: 600, marginTop: '0.4rem',
                }}
              >
                ← Go back
              </button>
            </div>
          ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: '#424242', fontSize: '0.9rem' }}>
                Email or Mobile Number
              </label>
              <input
                type="text"
                value={emailOrMobile}
                onChange={e => setEmailOrMobile(e.target.value)}
                required
                style={{
                  width: '100%', padding: '0.75rem 1rem', border: '2px solid #E0E0E0',
                  borderRadius: '8px', boxSizing: 'border-box', fontSize: '0.95rem',
                  transition: 'border-color 0.2s', outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = '#43A047'}
                onBlur={e => e.target.style.borderColor = '#E0E0E0'}
                placeholder="admin@dairy.com or 9876543210"
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: '#424242', fontSize: '0.9rem' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{
                  width: '100%', padding: '0.75rem 1rem', border: '2px solid #E0E0E0',
                  borderRadius: '8px', boxSizing: 'border-box', fontSize: '0.95rem',
                  transition: 'border-color 0.2s', outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = '#43A047'}
                onBlur={e => e.target.style.borderColor = '#E0E0E0'}
                placeholder="Enter your password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '0.85rem', background: loading ? '#A5D6A7' : '#43A047',
                color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem', fontWeight: 700, letterSpacing: '0.3px',
                boxShadow: '0 2px 8px rgba(67,160,71,0.3)', transition: 'all 0.2s',
              }}
              onMouseEnter={e => { if (!loading) (e.target as HTMLElement).style.background = '#388E3C'; }}
              onMouseLeave={e => { if (!loading) (e.target as HTMLElement).style.background = '#43A047'; }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            {googleClientId && (
              <>
                <div style={{ margin: '1rem 0 0.6rem', textAlign: 'center', color: '#777', fontSize: '0.82rem' }}>
                  SuperAdmin only: Sign in with Gmail
                </div>
                <div ref={el => { googleBtnRef.current = el; }} style={{ display: 'flex', justifyContent: 'center' }} />
                {!googleReady && <div style={{ textAlign: 'center', color: '#888', fontSize: '0.8rem', marginTop: '0.45rem' }}>Loading Google Sign-In...</div>}
              </>
            )}
          </form>
          )}
        </div>


      </div>
    </div>
  );
}
